import { createContext, useContext, useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { doc, onSnapshot, setDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./hooks/useAuth";
import LoginPage from "./pages/LoginPage";

const ProjectPage = lazy(() => import("./pages/ProjectPage"));
const ImportPage = lazy(() => import("./pages/ImportPage"));

const ProjectIdContext = createContext<string>("");
export function useProjectId() {
  return useContext(ProjectIdContext);
}

function Spinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading, authorized, logout } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!authorized) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-bone">
        <div className="text-center">
          <p className="text-sm font-semibold text-gray-900">Access denied</p>
          <p className="mt-1 text-xs text-gray-500">
            {user.email} is not on the allowed users list.
          </p>
        </div>
        <button onClick={logout} className="btn-ghost py-1 text-xs">
          Sign out
        </button>
      </div>
    );
  }
  return <>{children}</>;
}

function AppLoader({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [projectId, setProjectId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(db, "app/config"),
      async (snap) => {
        if (snap.exists()) {
          setProjectId(snap.data().projectId as string);
          setLoading(false);
        } else {
          try {
            // First run: bootstrap the single project and config
            const projectRef = await addDoc(collection(db, "projects"), {
              name: "Localization",
              languages: ["en"],
              referenceLanguage: "en",
              createdAt: serverTimestamp(),
              createdBy: user.uid,
            });
            await setDoc(doc(db, "app/config"), { projectId: projectRef.id });
            // onSnapshot will fire again and set projectId
          } catch (err) {
            console.error("Failed to bootstrap app config:", err);
            setLoading(false);
          }
        }
      },
      (err) => {
        console.error("Failed to read app/config:", err);
        setLoading(false);
      }
    );
  }, [user]);

  if (loading) return <Spinner />;
  return (
    <ProjectIdContext.Provider value={projectId!}>
      {children}
    </ProjectIdContext.Provider>
  );
}

function AuthenticatedApp({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <AppLoader>{children}</AppLoader>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/keys"
            element={<AuthenticatedApp><ProjectPage /></AuthenticatedApp>}
          />
          <Route
            path="/screenshots"
            element={<AuthenticatedApp><ProjectPage /></AuthenticatedApp>}
          />
          <Route
            path="/versions"
            element={<AuthenticatedApp><ProjectPage /></AuthenticatedApp>}
          />
          <Route
            path="/import"
            element={<AuthenticatedApp><ImportPage /></AuthenticatedApp>}
          />
          <Route path="/" element={<Navigate to="/keys" replace />} />
          <Route path="*" element={<Navigate to="/keys" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
